import _ from "lodash";
import Vue from "vue";
import Vuex from "vuex";

import { UserService, SettingsService } from "@/core/services";
import { MsalUtility } from "@/core/utilities/msal.utility";
import { PagedCollectionFilterDictionary } from "@/core/models";
import { appInsights } from "@/main";

Vue.use(Vuex);

const RoleListStorageKey = "iris-user-role-list";
const UserIdStorageKey = "iris-user-id";
const CanPerformQualityAuditsStorageKey = "iris-can-perform-quality-audits";
const PermanentNetworkFailureStorageKey = "iris-permanent-network-failure";
const NetworkFailureAttemptCountStorageKey = "iris-network-failure-attempt-count";
const NetworkFailurePermanentThreshold = 3; // Number of consecutive network failures before marking as permanent

function getRoleListFromStorage() {
    return sessionStorage.getItem(RoleListStorageKey);
}

function getUserIdFromStorage() {
    const id = sessionStorage.getItem(UserIdStorageKey);

    if (!!id) {
        return +id;
    }
}

function getCanPerformQualityAuditsFromStorage() {
    const value = sessionStorage.getItem(CanPerformQualityAuditsStorageKey);
    if (value == null) {
        return null;
    }

    return value === "true";
}

function setCanPerformQualityAuditsFromStorage(value: boolean) {
    const valueToStore = value
        ? "true"
        : "";

    sessionStorage.setItem(CanPerformQualityAuditsStorageKey, valueToStore);
}

function getPermanentNetworkFailureFromStorage() {
    const value = sessionStorage.getItem(PermanentNetworkFailureStorageKey);
    if (value == null) {
        return null;
    }

    return value === "true";
}

function setPermanentNetworkFailureToStorage(value: boolean) {
    const valueToStore = value ? "true" : "";
    sessionStorage.setItem(PermanentNetworkFailureStorageKey, valueToStore);
}

function getNetworkFailureAttemptCountFromStorage() {
    const value = sessionStorage.getItem(NetworkFailureAttemptCountStorageKey);
    if (value == null || value === "") {
        return 0;
    }
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
}

function setNetworkFailureAttemptCountToStorage(count: number) {
    const normalized = count > 0 ? String(count) : "";
    sessionStorage.setItem(NetworkFailureAttemptCountStorageKey, normalized);
}

/**
 * Vuex Store for Iris
 *
 * TODO: This store is NOT strongly typed. It should be.
 *   I did not have the time to look into and implementing strongly typing
 *   the store. Maybe we could do it in another project!
 *   Examples:
 *     - https://next.vuex.vuejs.org/guide/typescript-support.html
 *     - https://medium.com/swlh/properly-typed-vuex-stores-427bf4c6a3d1
 */
export default new Vuex.Store({
    state: {
        userRoleList: getRoleListFromStorage(),
        canPerformQualityAudits: getCanPerformQualityAuditsFromStorage(),
        // error flag used elsewhere in the store but previously not initialized
        error: false,
        isFetchingUserRoles: false,
        listFilters: {} as PagedCollectionFilterDictionary,
        permanentNetworkFailure: getPermanentNetworkFailureFromStorage(),
        networkFailureAttemptCount: getNetworkFailureAttemptCountFromStorage(),
    },
    getters: {
        userRoleList(state: any) {
            // TODO: Put a time limit (e.g. 5 minutes?) on the roles that can be stored
            // in state or storage.

            return state.userRoleList !== null
                ? state.userRoleList
                : getRoleListFromStorage();
        },
        canPerformQualityAudits(state: any) {
            return state.canPerformQualityAudits !== null
                ? state.canPerformQualityAudits
                : getCanPerformQualityAuditsFromStorage();
        },
        permanentNetworkFailure(state: any) {
            return state.permanentNetworkFailure !== null
                ? state.permanentNetworkFailure
                : getPermanentNetworkFailureFromStorage();
        },
        networkFailureAttemptCount(state: any) {
            return typeof state.networkFailureAttemptCount === "number"
                ? state.networkFailureAttemptCount
                : getNetworkFailureAttemptCountFromStorage();
        },
        userRoles(state: any, getters: any): string[] {
            if (!state.userRoleList) {
                return [];
            }

            return String(state.userRoleList).split(",");
        },
        userId(state: any) {
            return getUserIdFromStorage();
        },
        listFilters(state: any) {
            return state.listFilters;
        },
    },
    mutations: {
        setRoleList(state, payload) {
            state.userRoleList = payload;

            if (String(payload)) {
                sessionStorage.setItem(RoleListStorageKey, payload);
            } else {
                // There is nothing to store so remove values from localstorage
                sessionStorage.removeItem(RoleListStorageKey);
            }
        },
        setCanPerformQualityAudits(state, payload) {
            state.canPerformQualityAudits = payload;
            setCanPerformQualityAuditsFromStorage(payload);
        },
        setPermanentNetworkFailure(state, payload: boolean) {
            state.permanentNetworkFailure = payload;
            setPermanentNetworkFailureToStorage(payload);
        },
        incrementNetworkFailureAttemptCount(state) {
            const current = typeof state.networkFailureAttemptCount === "number"
                ? state.networkFailureAttemptCount
                : 0;
            const next = current + 1;
            state.networkFailureAttemptCount = next;
            setNetworkFailureAttemptCountToStorage(next);
        },
        resetNetworkFailureAttempts(state) {
            state.networkFailureAttemptCount = 0;
            setNetworkFailureAttemptCountToStorage(0);
        },
        setUserId(state, payload) {
            state.userId = payload;

            if (String(payload)) {
                sessionStorage.setItem(UserIdStorageKey, payload);
                appInsights.setAuthenticatedUserContext(`${payload}`);
            } else {
                // There is nothing to store so remove values from localstorage
                appInsights.clearAuthenticatedUserContext();
                sessionStorage.removeItem(UserIdStorageKey);
            }
        },
        setListFilters(state, payload) {
            state.listFilters = payload;
        },
    },
    actions: {
        setUserRoles({ commit, state }) {
            if (state.isFetchingUserRoles || state.error || state.permanentNetworkFailure) {
                return;
            }

            state.isFetchingUserRoles = true;
            UserService
                .getMyUserInfo()
                .then((userInfo) => {
                    state.error = false;
                    // On successful call, clear any network failure status and attempts
                    commit("setPermanentNetworkFailure", false);
                    commit("resetNetworkFailureAttempts");
                    commit("setUserId", userInfo.userId);
                    commit("setRoleList", userInfo.roles);
                    commit("setCanPerformQualityAudits", userInfo.canPerformQualityAudits);
                })
                .catch((e) => {

                    if (e && e.code && e.code == "ERR_NETWORK") {
                        // Network failure: set error and track attempts; after a threshold mark as permanent
                        state.error = true;

                        const nextCount = (typeof state.networkFailureAttemptCount === "number" ? state.networkFailureAttemptCount : 0) + 1;
                        commit("incrementNetworkFailureAttemptCount");

                        const isOffline = typeof navigator !== "undefined" && navigator && (navigator as any).onLine === false;
                        if (isOffline || nextCount >= NetworkFailurePermanentThreshold) {
                            commit("setPermanentNetworkFailure", true);
                        }

                        return;
                    }
                    if (e && e.response && e.response.status === 401) {
                        // This user does not have the permissions to access the backend
                        const noAccessUrl = "/" + SettingsService.getAppOwnerNoAccessPage();
                        MsalUtility.logout(noAccessUrl);
                    }
                })
                .finally(() => {
                    state.isFetchingUserRoles = false;
                });
        },

        clearUserRoles({ commit, state }) {
            commit("setRoleList", "");
            commit("setUserId", "");
            // Reset network failure tracking upon explicit clear
            commit("setPermanentNetworkFailure", false);
            commit("resetNetworkFailureAttempts");
            state.error = false;
        },
    },
});
